// The `evidence/leaderboard` team-wide usage aggregate — carved from
// `src/hooks/query/useQueryTeamEvidenceUsageStats.ts` (now a bodied delegate).
//
// Same two severances as its per-user sibling: S1 (the inline `scribeHttpV2`
// request moved into `../api/userActivity`) and S2 (the `token && enabled` half
// of the gate collapsed into one delegate-computed boolean).
//
// `teamId` DID NOT COLLAPSE WITH THE TOKEN, and the split is the reusable part.
// The pre-move gate was `token && enabled && teamId`: the first two are app
// facts and cross as one boolean, but the third is this hook's own ARGUMENT and
// also the second element of its cache identity. Hoisting it to the delegate
// would let a caller pass a truthy gate with no id and GET
// `/user-activity/team/undefined/evidence`.
import { skipToken, useQuery } from '@tanstack/react-query';

import { TEAM_EVIDENCE_USAGE_STATS_QUERY_KEY } from '../../contracts';
import { getTeamEvidenceUsageStats } from '../api/userActivity';

/**
 * GET /api/v2/ml-scribe/user-activity/team/{team_id}/evidence
 *
 * Aggregate evidence usage stats summed across every member of a team — used
 * on team-admin surfaces such as the 90-day trial recap modal so the recap
 * reflects the whole practice's engagement, not just the admin's personal
 * usage.
 *
 * The endpoint returns 403 for non-admin callers, so consumers must gate the
 * call upstream by passing `enabled: false` when the current user isn't an
 * admin of the team.
 *
 * @param enabled - App-resolved gate (`!!token && <caller's own enabled>`),
 *   already reduced to one decision by the delegate.
 */
export function useQueryTeamEvidenceUsageStats(
  teamId: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: [TEAM_EVIDENCE_USAGE_STATS_QUERY_KEY, { teamId }],
    queryFn:
      enabled && teamId ? async () => getTeamEvidenceUsageStats(teamId) : skipToken,
    staleTime: 60 * 1000,
  });
}
